import { createFileRoute } from "@tanstack/react-router";
import { Button, Spinner, Surface } from "@heroui/react";
import { StorageGrantType } from "@repo/api";
import { formatTime } from "@repo/utils/tools";
import StorageQuotaBar from "@repo/ui/pages/template_service/storage/StorageQuotaBar";
import {
  formatStorageGB,
  isStorageQuotaExpiringSoon,
} from "@repo/ui/pages/template_service/storage/quota";
import { SafeAreaContainer } from "@/components/layout/safe-area-container";
import { useStorageQuota } from "@repo/ui/pages/template_service/storage/useStorageQuota";
import StorageFileUsageList from "./-components/StorageFileUsageList";
import PurchaseStorageModal from "./-components/PurchaseStorageModal";

export const Route = createFileRoute("/template_protections/storage")({
  component: StorageUsagePage,
});

export default function StorageUsagePage() {
  const { data: quota, isLoading, refetch } = useStorageQuota();
  const packs = quota?.items ?? [];

  return (
    <SafeAreaContainer customPaddingBottom="2rem">
      <div className="flex flex-col gap-4 px-4 pt-3 pb-6">
        <Surface variant="default" className="flex flex-col gap-4 rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-foreground">云空间用量</h2>
              <p className="mt-1 text-sm text-muted">
                查看当前容量占用，以及您拥有的扩容包与有效期
              </p>
            </div>
            <PurchaseStorageModal
              onPaid={() => void refetch()}
              trigger={
                <Button className="shrink-0 rounded-lg" size="sm">
                  购买扩容
                </Button>
              }
            />
          </div>

          <StorageQuotaBar
            size="lg"
            label="已使用"
            used={quota?.used ?? 0}
            count={quota?.count ?? 0}
            isPending={isLoading}
          />

          {!isLoading && quota && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-default-100 px-2 py-3">
                <p className="text-xs text-muted">总量</p>
                <p className="mt-1 text-sm font-semibold text-foreground tabular-nums">
                  {formatStorageGB(quota.count)}
                </p>
              </div>
              <div className="rounded-lg bg-default-100 px-2 py-3">
                <p className="text-xs text-muted">已用</p>
                <p className="mt-1 text-sm font-semibold text-foreground tabular-nums">
                  {formatStorageGB(quota.used)}
                </p>
              </div>
              <div className="rounded-lg bg-default-100 px-2 py-3">
                <p className="text-xs text-muted">剩余</p>
                <p className="mt-1 text-sm font-semibold text-foreground tabular-nums">
                  {formatStorageGB(quota.remain)}
                </p>
              </div>
            </div>
          )}
        </Surface>

        <Surface variant="default" className="flex flex-col gap-3 rounded-xl p-4">
          <h2 className="text-base font-semibold text-foreground">我的容量包</h2>

          {isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : packs.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">暂无扩容包</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {packs.map((pack, index) => (
                <li
                  key={`${pack.quota}-${pack.expire}-${index}`}
                  className="flex items-center justify-between gap-3 rounded-lg bg-default-100 px-3 py-3"
                >
                  <div className="min-w-0 flex flex-col gap-1">
                    <span className="text-sm font-medium text-foreground">
                      {formatStorageGB(pack.quota)} 扩容包
                    </span>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                      {pack.grant_type === StorageGrantType.Gift && (
                        <span className="text-success">免费赠送</span>
                      )}
                      {pack.grant_type === StorageGrantType.Purchase && (
                        <span className="text-muted">购买获得</span>
                      )}
                      {isStorageQuotaExpiringSoon(pack.expire) && (
                        <span className="text-warning">即将到期</span>
                      )}
                    </div>
                  </div>
                  <span className="shrink-0 text-right text-xs text-muted">
                    {pack.expire ? (
                      <>
                        <span className="block">有效期至</span>
                        <span className="block tabular-nums">
                          {formatTime(pack.expire, "YYYY-MM-DD")}
                        </span>
                      </>
                    ) : (
                      "永久有效"
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Surface>

        <Surface variant="default" className="rounded-xl p-4">
          <StorageFileUsageList />
        </Surface>
      </div>
    </SafeAreaContainer>
  );
}
